// frontend/src/components/UI/Header.js

import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../../context/AuthContext';
import axiosInstance from '../../utils/axiosConfig';

/** Hilfsfunktion: Kategorieliste in eine gruppierte Baumstruktur umwandeln */
function groupCategories(cats) {
  const map = {};
  cats.forEach((cat) => {
    map[cat._id] = { ...cat, subcategories: [] };
  });
  const topLevel = [];
  cats.forEach((cat) => {
    if (!cat.parent) {
      topLevel.push(map[cat._id]);
    } else {
      const pid = cat.parent._id || cat.parent;
      if (map[pid]) {
        map[pid].subcategories.push(map[cat._id]);
      }
    }
  });
  return topLevel;
}

/** Rollen ins Deutsche übersetzen */
function translateRole(role) {
  switch (role) {
    case 'Admin':
      return 'Administrator';
    case 'Editor':
      return 'Redaktor';
    case 'Author':
      return 'Autor';
    case 'Reader':
      return 'Leser';
    default:
      return role;
  }
}

const Header = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);

  // Zustand für mobiles Menü (offen/zu)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Dropdown-Zustände (Desktop)
  const [showAbout, setShowAbout] = useState(false);
  const [showCats, setShowCats] = useState(false);

  // Wir verwenden dieselben Zustände für Mobilgeräte
  const [topCategories, setTopCategories] = useState([]);

  // Suchleiste
  const [query, setQuery] = useState('');
  const [autocompleteResults, setAutocompleteResults] = useState([]);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const autocompleteRef = useRef(null);

  // Kategorien beim Laden holen
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axiosInstance.get('/categories');
        setTopCategories(groupCategories(data));
      } catch {}
    })();
  }, []);

  // Mobiles Menü umschalten
  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  // Desktop: "Über uns" öffnen => schliesst Kategorien
  const toggleAbout = () => {
    setShowCats(false);
    setShowAbout((prev) => !prev);
  };

  // Desktop: "Kategorien" öffnen => schliesst "Über uns"
  const toggleCats = () => {
    setShowAbout(false);
    setShowCats((prev) => !prev);
  };

  // Such-Eingabe
  const handleSearchChange = async (e) => {
    const val = e.target.value;
    setQuery(val);
    if (val.trim().length >= 1) {
      try {
        const { data } = await axiosInstance.get('/articles/search', {
          params: { query: val.trim() },
        });
        setAutocompleteResults(data.slice(0, 5));
        setShowAutocomplete(true);
      } catch {}
    } else {
      setAutocompleteResults([]);
      setShowAutocomplete(false);
    }
  };

  // Suche absenden
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setShowAutocomplete(false);
      navigate(`/?search=${encodeURIComponent(query.trim())}`);
    }
  };

  // Autocomplete schliessen, wenn ausserhalb geklickt wird
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(event.target)
      ) {
        setShowAutocomplete(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mobile: "Über uns" umschalten => schliesst Kategorien
  const toggleMobileAbout = () => {
    setShowCats(false);
    setShowAbout((prev) => !prev);
  };

  // Mobile: "Kategorien" umschalten => schliesst "Über uns"
  const toggleMobileCats = () => {
    setShowAbout(false);
    setShowCats((prev) => !prev);
  };

  return (
    <header className="bg-white shadow relative">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* LOGO */}
        <div>
          <Link to="/">
            <img src="/logo.png" alt="Logo" className="h-8 w-auto" />
          </Link>
        </div>

        {/* Navigation (Desktop) */}
        <nav className="hidden sm:flex items-center space-x-6">
          <Link to="/" className="text-gray-600 hover:text-gray-900">
            Startseite
          </Link>

          {/* Über uns - Dropdown (Desktop) */}
          <div className="relative">
            <button
              onClick={toggleAbout}
              className="text-gray-600 hover:text-gray-900 inline-flex items-center"
            >
              Über uns
              <ChevronDownIcon className="w-4 h-4 ml-1" />
            </button>
            {showAbout && (
              <div className="absolute bg-white shadow rounded mt-2 w-48 z-10 p-2">
                <Link
                  to="/about-us"
                  className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded"
                  onClick={() => setShowAbout(false)}
                >
                  Über das Team
                </Link>
                <Link
                  to="/contact"
                  className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded"
                  onClick={() => setShowAbout(false)}
                >
                  Kontakt
                </Link>
              </div>
            )}
          </div>

          {/* Kategorien - Dropdown (Desktop) */}
          <div className="relative">
            <button
              onClick={toggleCats}
              className="text-gray-600 hover:text-gray-900 inline-flex items-center"
            >
              Kategorien
              <ChevronDownIcon className="w-4 h-4 ml-1" />
            </button>
            {showCats && (
              <div className="absolute bg-white shadow rounded mt-2 w-64 z-10 p-2">
                {topCategories.map((cat) => {
                  const hasSub = cat.subcategories && cat.subcategories.length;
                  return (
                    <div key={cat._id} className="relative group mb-2">
                      <Link
                        to={`/category/${cat.slug}`}
                        className="block px-3 py-2 text-gray-700 hover:bg-gray-100 rounded"
                        onClick={() => setShowCats(false)}
                      >
                        {cat.name}
                        {hasSub > 0 && (
                          <ChevronDownIcon className="w-4 h-4 inline-block ml-1 text-gray-500 group-hover:text-gray-700" />
                        )}
                      </Link>
                      {hasSub > 0 && (
                        <div className="absolute hidden group-hover:block top-0 right-full mr-1 bg-white shadow rounded p-2 w-48">
                          {cat.subcategories.map((sub) => (
                            <Link
                              key={sub._id}
                              to={`/category/${sub.slug}`}
                              className="block px-2 py-1 text-gray-600 hover:bg-gray-100 rounded"
                              onClick={() => setShowCats(false)}
                            >
                              {sub.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Anmeldung / Rollen (Desktop) */}
          {user ? (
            <>
              <Link
                to={`/${user.role.toLowerCase()}/dashboard`}
                className="text-gray-600 hover:text-gray-900"
              >
                {translateRole(user.role)}
              </Link>
              <button
                onClick={logout}
                className="text-gray-600 hover:text-gray-900"
              >
                Abmelden
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-gray-600 hover:text-gray-900">
                Anmelden
              </Link>
              <Link to="/register" className="text-gray-600 hover:text-gray-900">
                Registrieren
              </Link>
            </>
          )}

          {/* Suchfeld (Desktop) */}
          <form
            onSubmit={handleSearchSubmit}
            className="relative"
            ref={autocompleteRef}
          >
            <input
              type="text"
              className="border px-3 py-2 rounded focus:outline-none w-32"
              placeholder="Suchen..."
              value={query}
              onChange={handleSearchChange}
              onFocus={() => {
                if (autocompleteResults.length > 0) {
                  setShowAutocomplete(true);
                }
              }}
            />
            {showAutocomplete && autocompleteResults.length > 0 && (
              <div className="absolute left-0 mt-1 w-full bg-white border border-gray-200 rounded shadow-md z-50">
                {autocompleteResults.map((art) => (
                  <button
                    key={art._id}
                    type="button"
                    onClick={() => {
                      setShowAutocomplete(false);
                      navigate(`/articles/${art.slug}`);
                    }}
                    className="block w-full text-left px-3 py-2 hover:bg-gray-100"
                  >
                    {art.title}
                  </button>
                ))}
              </div>
            )}
          </form>
        </nav>

        {/* Mobile-Menü Button */}
        <button
          onClick={toggleMobileMenu}
          className="sm:hidden text-gray-600 hover:text-gray-900"
        >
          {isMobileMenuOpen ? (
            <XMarkIcon className="w-6 h-6" />
          ) : (
            <Bars3Icon className="w-6 h-6" />
          )}
        </button>
      </div>

      {/* Mobile-Menü */}
      {isMobileMenuOpen && (
        <div className="sm:hidden bg-white shadow p-4">
          {/* Über uns (Mobile) */}
          <div className="mb-2">
            <button
              onClick={() => {
                toggleMobileAbout();
              }}
              className="w-full text-left py-2 text-gray-700 hover:bg-gray-50 font-semibold inline-flex items-center"
            >
              Über uns
              <ChevronDownIcon className="w-4 h-4 ml-1" />
            </button>
            {showAbout && (
              <div className="ml-4 mt-1 border-l border-gray-200 pl-2 space-y-1">
                <Link
                  to="/about-us"
                  onClick={() => {
                    setShowAbout(false);
                    setIsMobileMenuOpen(false);
                  }}
                  className="block py-1 text-gray-700 hover:bg-gray-50"
                >
                  Über das Team
                </Link>
                <Link
                  to="/contact"
                  onClick={() => {
                    setShowAbout(false);
                    setIsMobileMenuOpen(false);
                  }}
                  className="block py-1 text-gray-700 hover:bg-gray-50"
                >
                  Kontakt
                </Link>
              </div>
            )}
          </div>

          {/* Kategorien (Mobile) */}
          <div className="mb-2">
            <button
              onClick={() => {
                toggleMobileCats();
              }}
              className="w-full text-left py-2 text-gray-700 hover:bg-gray-50 font-semibold inline-flex items-center"
            >
              Kategorien
              <ChevronDownIcon className="w-4 h-4 ml-1" />
            </button>
            {showCats && (
              <div className="ml-4 mt-1 border-l border-gray-200 pl-2">
                {topCategories.map((cat) => (
                  <div key={cat._id} className="mb-1">
                    <Link
                      to={`/category/${cat.slug}`}
                      onClick={() => {
                        setShowCats(false);
                        setIsMobileMenuOpen(false);
                      }}
                      className="block px-2 py-1 text-gray-700 hover:bg-gray-50 rounded"
                    >
                      {cat.name}
                    </Link>
                    {cat.subcategories?.length > 0 && (
                      <div className="ml-4">
                        {cat.subcategories.map((sub) => (
                          <Link
                            key={sub._id}
                            to={`/category/${sub.slug}`}
                            onClick={() => {
                              setShowCats(false);
                              setIsMobileMenuOpen(false);
                            }}
                            className="block px-2 py-1 text-sm text-gray-600 hover:bg-gray-50 rounded"
                          >
                            {sub.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Auth Links (Mobile) */}
          {user ? (
            <>
              <Link
                to={`/${user.role.toLowerCase()}/dashboard`}
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-2 text-gray-700 hover:bg-gray-50"
              >
                {translateRole(user.role)}
              </Link>
              <button
                onClick={() => {
                  logout();
                  setIsMobileMenuOpen(false);
                }}
                className="block py-2 text-gray-700 hover:bg-gray-50"
              >
                Abmelden
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-2 text-gray-700 hover:bg-gray-50"
              >
                Anmelden
              </Link>
              <Link
                to="/register"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block py-2 text-gray-700 hover:bg-gray-50"
              >
                Registrieren
              </Link>
            </>
          )}

          {/* Suchfeld (Mobile) */}
          <div className="mt-4" ref={autocompleteRef}>
            <form onSubmit={handleSearchSubmit} className="relative">
              <input
                type="text"
                className="border px-3 py-2 rounded w-full focus:outline-none"
                placeholder="Suchen..."
                value={query}
                onChange={handleSearchChange}
                onFocus={() => {
                  if (autocompleteResults.length > 0) {
                    setShowAutocomplete(true);
                  }
                }}
              />
              {showAutocomplete && autocompleteResults.length > 0 && (
                <div className="absolute left-0 mt-1 w-full bg-white border border-gray-200 rounded shadow-md z-50">
                  {autocompleteResults.map((art) => (
                    <button
                      key={art._id}
                      type="button"
                      onClick={() => {
                        setShowAutocomplete(false);
                        setIsMobileMenuOpen(false);
                        navigate(`/articles/${art.slug}`);
                      }}
                      className="block w-full text-left px-3 py-2 hover:bg-gray-100"
                    >
                      {art.title}
                    </button>
                  ))}
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
